import { Box, Typography, Chip, Stack, Button } from "@mui/material";

const getPriorityStyles = (priority) => {
  switch (priority) {
    case "High":
      return {
        chipBg: "#FEF2F2",
        chipColor: "#C10007",
        chipBorder: "1px solid #C10007",
      };
    case "Medium":
      return {
        chipBg: "#FEF3C6",
        chipColor: "#E17100",
        chipBorder: "1px solid #E17100",
      };
    case "Low":
      return {
        chipBg: "#DCFCE7",
        chipColor: "#00A63E",
        chipBorder: "1px solid #00A63E",
      };
    default:
      return {
        chipBg: "#F3F4F6",
        chipColor: "#374151",
        chipBorder: "1px solid #374151",
      };
  }
};

const RequestCard = ({ request, onApprove, onReject }) => {
  const priorityStyles = getPriorityStyles(request.priority);

  return (
    <Box
      sx={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
      }}
    >
      {/* Title + Priority chip */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        {/* Title + Location */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box>
            <Chip
              label={request.requestNumber || request.id}
              sx={{
                bgcolor: "#45556C",
                color: "#fff",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "24px",
                borderRadius: "8px",
                flexShrink: 0,
                fontFamily:
                  'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              }}
            />
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              lineHeight: "24px",
              color: "#020618",
            }}
          >
            {request.title}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: "20px",
              color: "#62748E",
            }}
          >
            {request.location}
          </Typography>
        </Box>

        {/* Priority chip */}
        <Chip
          label={request.priority}
          sx={{
            bgcolor: priorityStyles.chipBg,
            color: priorityStyles.chipColor,
            border: priorityStyles.chipBorder,
            fontWeight: 600,
            fontSize: "12px",
            borderRadius: 0.6,
            height: "20px",
            flexShrink: 0,
            mt: "4px",
          }}
        />
      </Box>

      {/* Description */}
      <Typography
        sx={{
          fontSize: "14px",
          lineHeight: "20px",
          fontWeight: 400,
          color: "#020618",
        }}
      >
        {request.description}
      </Typography>

      {/* Details row: Due, Cost, Parts */}
      <Stack direction="row" spacing={4} sx={{ mt: 0.5 }}>
        {[
          { label: "Due", value: request.dueDate },
          {
            label: "Cost",
            value: request.cost ? `$${request.cost.toLocaleString()}` : "—",
          },
          {
            label: "Parts",
            value: request.parts ? `${request.parts} items` : "—",
          },
        ].map((detail) => (
          <Box key={detail.label} sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: "#020618",
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "24px",
                mb: 1,
              }}
            >
              {detail.label}
            </Typography>
            <Typography
              sx={{
                color: "#020618",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "24px",
              }}
            >
              {detail.value}
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* Approve / Reject Buttons */}
      <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
        <Button
          variant="outlined"
          sx={{
            flex: 1,
            height: 39,
            background: "#F0FDF4",
            border: "1px solid #008236",
            color: "#008236",
            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: { xs: "0.875rem", md: "0.9375rem" },
            textTransform: "none",
            "&:hover": {
              border: "1px solid #008236",
              background: "#F0FDF4",
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            onApprove(request.id);
          }}
        >
          Approve
        </Button>
        <Button
          variant="outlined"
          sx={{
            flex: 1,
            height: 39,
            background: "#FEF2F2",
            border: "1px solid #C10007",
            color: "#C10007",
            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: { xs: "0.875rem", md: "0.9375rem" },
            textTransform: "none",
            "&:hover": {
              border: "1px solid #C10007",
              background: "#FEF2F2",
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            onReject(request.id);
          }}
        >
          Reject
        </Button>
      </Stack>
    </Box>
  );
};

export default RequestCard;
